import {ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {ColorSketchModule} from 'ngx-color/sketch';
import {CommonModule} from '@angular/common';
import {GraphMap} from '../api/interfaces/graph-node.interface';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {AiStateService} from '../services/ai-state.service';
import {environment} from '../../environments/environment';
import {MatSelectModule} from '@angular/material/select';
import {MapService} from '../api/services/map.service';
import {UserService} from '../api/services/user.service';
import {MatIconModule} from '@angular/material/icon';
import {TextInputComponent} from '../shared/forms/text-input/text-input.component';
import {MatTooltipModule} from '@angular/material/tooltip';
import {SlideToggleInputComponent} from '../shared/forms/slide-select/slide-toggle-input.component';
import {SelectInputComponent} from '../shared/forms/select-input/select-input.component';
import {BehaviorSubject} from 'rxjs';
import {TextAreaComponent} from '../shared/forms/text-area/text-area-input.component';
import {set} from 'lodash';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {HasPackageDirective} from '../packages/has-package.directive';
import {DOCUMENT_MAX_MB, documentFormatGlyph, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {isPdfTooLargeError, showPdfTooLargeNotification} from './pdf-too-large.util';
import {PackageService} from '../api/services/package.service';
import {ModelService} from '../api/services/model.service';
import {MatTabsModule} from '@angular/material/tabs';
import {MatFormFieldModule} from '@angular/material/form-field';
import {OpenaiService} from '../api/services/openai.service';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {Router} from '@angular/router';
import {DraftUploadService} from '../document-draft/draft-upload.service';
import {NotificationService} from '../services/notification.service';
import {CreditApiService} from '../api/services/credit.service';
import {EndTrialFrameComponent} from './end-trial-frame.component';
import {TrialStateService} from '../services/trial-state.service';

const TAB_STORAGE_KEY = 'maporium_create_smap_active_tab';

@Component({
  selector: 'maporium-new-map-dialog',
  template: `
    <!-- EDIT / COPY MODE: original form without tabs -->
    @if (isEditOrCopyMode) {
      <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
        <h2 class="align-items-center d-flex m-0 p-0">
          <span *ngIf="!data?.isCopy" class="material-symbols-rounded me-2">
            {{ data?.map && !data?.isCopy ? 'edit' : 'add_circle' }}
          </span>
          <span *ngIf="data?.isCopy" class="material-symbols-rounded me-2">file_copy</span>
          <span *ngIf="!data?.isCopy">
            {{ data?.map ? ('CREATE_EDIT_SMAP.TITLE.EDIT' | translate) : ('CREATE_EDIT_SMAP.TITLE.CREATE' | translate) }}
          </span>
          <span *ngIf="data?.isCopy">
            {{ 'CREATE_MAP_DIALOG.DUPLICATE_TITLE' | translate:{ name: data?.map?.name } }}
          </span>
        </h2>
        <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
      </div>

      <div mat-dialog-content>
        <form [formGroup]="myForm">
          <app-text-input
            [label]="'CREATE_EDIT_SMAP.FIELDS.NAME' | translate"
            formControlName="name"
            [autoFocusAndSelect]="true">
          </app-text-input>

          <app-text-area
            [label]="'CREATE_EDIT_SMAP.FIELDS.DESCRIPTION' | translate"
            formControlName="description"
            [autoFocusAndSelect]="false">
          </app-text-area>

          <app-slide-toggle-input
            *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]"
            [label]="'CREATE_EDIT_SMAP.TOGGLE.USE_AS_TEMPLATE' | translate"
            [ngClass]="{'hidden': !data?.map || data?.isCopy}"
            formControlName="isTemplate"
            [matTooltip]="'CREATE_EDIT_SMAP.TOOLTIP.USE_AS_TEMPLATE' | translate">
          </app-slide-toggle-input>

          <app-slide-toggle-input
            *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]"
            [label]="'CREATE_MAP_DIALOG.ALLOW_VIEWERS_CREATE_LABEL' | translate"
            [ngClass]="{'hidden': !data?.map || data?.isCopy || !isMapATemplate }"
            formControlName="allowViewersToCreateFromTemplate"
            [matTooltip]="'CREATE_MAP_DIALOG.ALLOW_VIEWERS_CREATE_TOOLTIP' | translate">
          </app-slide-toggle-input>

          <ng-container *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]">
            <app-slide-toggle-input
              [label]="'CREATE_EDIT_SMAP.TOGGLE.INCLUDE_VIEWS_TOURS_TEMPLATE' | translate"
              formControlName="includeViewsAndTours"
              *ngIf="(data?.map && isMapATemplate) || (!data?.map && selectedTemplate)"
              [matTooltip]="'CREATE_EDIT_SMAP.TOOLTIP.INCLUDE_VIEWS_TOURS_TEMPLATE' | translate">
            </app-slide-toggle-input>
          </ng-container>
          <app-slide-toggle-input
            [label]="'CREATE_EDIT_SMAP.TOGGLE.INCLUDE_VIEWS_TOURS_COPY' | translate"
            *ngIf="data?.map && data?.isCopy"
            formControlName="includeViewsAndTours"
            [matTooltip]="'CREATE_EDIT_SMAP.TOOLTIP.INCLUDE_VIEWS_TOURS_COPY' | translate">
          </app-slide-toggle-input>
          <!-- MAP-1245: starter-smap selection lives in the admin tool now -->

          <app-select-input
            *ngIf="canSetPublisherName"
            [label]="'CREATE_MAP_DIALOG.PUBLISH_UNDER_LABEL' | translate"
            formControlName="publisherNameOption"
            [options]="publisherNameOptions"
            [isMaporiumIcon]="true">
          </app-select-input>
        </form>
      </div>

      <div mat-dialog-actions class="d-flex justify-content-evenly">
        <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close>{{ 'CREATE_MAP_DIALOG.CANCEL' | translate }}</button>
        <button class="dialog-action-btn dialog-action-btn-primary" (click)="data?.map && !data?.isCopy ? edit() : create()"
                [disabled]="!myForm.valid"
                cdkFocusInitial>
          <span *ngIf="!data?.isCopy">
            {{ data?.map ? ('CREATE_MAP_DIALOG.UPDATE' | translate) : ('CREATE_MAP_DIALOG.CREATE' | translate) }}
          </span>
          <span *ngIf="data?.isCopy">{{ 'CREATE_MAP_DIALOG.DUPLICATE' | translate }}</span>
        </button>
      </div>
    }

    <!-- CREATE MODE: tabbed interface -->
    @if (!isEditOrCopyMode) {
      <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
        <h2 class="align-items-center d-flex m-0 p-0">
          <span class="icon-smap-action-create me-2"></span>
          {{ 'CREATE_EDIT_SMAP.TITLE.CREATE' | translate }}
        </h2>
        <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
      </div>

      <div mat-dialog-content class="tabbed-content">
        <mat-tab-group
          [selectedIndex]="activeTabIndex"
          (selectedIndexChange)="onTabChange($event)"
          (animationDone)="focusActiveTabField()"
          class="create-smap-tabs">

          <!-- FROM TEMPLATE tab -->
          <mat-tab>
            <ng-template mat-tab-label>
              <span class="icon-smap-object-template"></span>{{ 'CREATE_MAP_DIALOG.TAB_TEMPLATE' | translate }}
            </ng-template>
            <div class="tab-body">
              <form [formGroup]="myForm">
                <app-select-input
                  [label]="'CREATE_EDIT_SMAP.TEMPLATE.TITLE' | translate"
                  [options]="templateOptionsStream | async"
                  [hasBlank]="false"
                  [formControl]="templateControl"
                  [isMaporiumIcon]="true"
                  [matTooltip]="'CREATE_EDIT_SMAP.TOOLTIP.CHOOSE_TEMPLATE' | translate"
                  [matTooltipPosition]="'right'"
                  [tooltipPosition]="'right'">
                </app-select-input>

                <app-text-input
                  #templateNameInput
                  [label]="'CREATE_EDIT_SMAP.FIELDS.NAME' | translate"
                  formControlName="name"
                  [autoFocusAndSelect]="true">
                </app-text-input>

                <app-text-area
                  [label]="'CREATE_EDIT_SMAP.FIELDS.DESCRIPTION' | translate"
                  formControlName="description"
                  [autoFocusAndSelect]="false">
                </app-text-area>

                <!-- MAP-1245: starter-smap selection lives in the admin tool now -->
              </form>
            </div>
          </mat-tab>

          <!-- CREATE WITH AI tab: a prompt, a document, or both, continued as a conversation -->
          <mat-tab *ngIf="aiStateService.isAiVisible()">
            <ng-template mat-tab-label>
              <span class="icon-smap-object-prompt"></span>{{ 'CREATE_MAP_DIALOG.TAB_AI' | translate }}
            </ng-template>
            <div class="tab-body">
              <div class="prompt-area">
                <textarea
                  #promptTextarea
                  class="prompt-textarea"
                  [formControl]="promptControl"
                  rows="6"
                  data-testid="create-ai-prompt"
                  [placeholder]="'CREATE_MAP_DIALOG.AI_PLACEHOLDER' | translate"></textarea>
                <div class="document-row">
                  <input #pdfFileInput type="file" name="file" id="pdfFile" accept=".pdf,.docx,.pptx,.txt,.md" hidden
                         (change)="onFileSelected()" data-testid="create-ai-file">
                  @if (selectedFileName) {
                    <span [class]="'icon-' + documentGlyph(selectedFileName) + ' document-icon'"></span>
                    <span class="document-name" [title]="selectedFileName">{{ selectedFileName }}</span>
                    <button type="button" class="document-remove" (click)="clearFile()"
                            [attr.aria-label]="'DOCUMENT_DRAFT.REMOVE_DOCUMENT' | translate: {name: selectedFileName}"
                            data-testid="create-ai-file-remove">×</button>
                  } @else {
                    <button type="button" class="choose-file-btn" (click)="pdfFileInput.click()" data-testid="create-ai-attach">
                      <span class="material-icons" aria-hidden="true">attach_file</span>
                      {{ 'CREATE_MAP_DIALOG.ATTACH_DOCUMENT' | translate }}
                    </button>
                    <span class="document-note">{{ 'CREATE_MAP_DIALOG.ATTACH_SOURCE_NOTE' | translate }}</span>
                  }
                </div>
                <p class="document-hint">{{ 'CREATE_MAP_DIALOG.DOCUMENT_LIMIT' | translate: {maxSizeMb: documentMaxMb} }}</p>
                <div class="toggle-row" *ngIf="webSearchAvailable">
                  <mat-slide-toggle
                    [(ngModel)]="webSearchEnabled"
                    [disabled]="webFeaturesLocked"
                    (change)="onWebSearchChange($event.checked)"
                    labelPosition="before"
                    [matTooltip]="'CREATE_MAP_DIALOG.SEARCH_WEB_TOOLTIP' | translate">
                    {{ 'CREATE_MAP_DIALOG.SEARCH_WEB' | translate }}
                  </mat-slide-toggle>
                  <mat-slide-toggle
                    [(ngModel)]="includeResourcesEnabled"
                    [disabled]="webFeaturesLocked"
                    (change)="onIncludeResourcesChange($event.checked)"
                    labelPosition="before"
                    [matTooltip]="'CREATE_MAP_DIALOG.INCLUDE_RESOURCES_TOOLTIP' | translate">
                    {{ 'CREATE_MAP_DIALOG.INCLUDE_RESOURCES' | translate }}
                  </mat-slide-toggle>
                  <mat-slide-toggle
                    [(ngModel)]="includeImagesEnabled"
                    [disabled]="webFeaturesLocked"
                    (change)="onIncludeImagesChange($event.checked)"
                    labelPosition="before"
                    [matTooltip]="'CREATE_MAP_DIALOG.INCLUDE_IMAGES_TOOLTIP' | translate">
                    {{ 'CREATE_MAP_DIALOG.INCLUDE_IMAGES' | translate }}
                  </mat-slide-toggle>
                </div>
                <maporium-end-trial-frame
                  class="trial-frame"
                  *ngIf="webSearchAvailable && webFeaturesLocked"
                  [text]="'CREATE_MAP_DIALOG.TRIAL_WEB_FEATURES' | translate"
                  [requireCardOnFile]="false"
                  (trialEnded)="onTrialEnded()">
                </maporium-end-trial-frame>
                <p class="web-search-cost-warning"
                   *ngIf="webSearchAvailable && !webFeaturesLocked && webSearchEnabled">
                  {{ 'CREATE_MAP_DIALOG.WEB_SEARCH_COST_WARNING' | translate }}
                </p>
              </div>
            </div>
          </mat-tab>

        </mat-tab-group>
      </div>

      <div mat-dialog-actions class="d-flex justify-content-evenly">
        <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close [disabled]="isImporting">{{ 'CREATE_MAP_DIALOG.CANCEL' | translate }}</button>
        <button class="dialog-action-btn dialog-action-btn-primary"
                [disabled]="!isCreateEnabled() || isImporting"
                (click)="onCreateClick()">
          {{ isImporting ? ('CREATE_MAP_DIALOG.STARTING' | translate) : ((activeTabIndex === 1 ? 'CREATE_MAP_DIALOG.START_CONVERSATION' : 'CREATE_MAP_DIALOG.CREATE') | translate) }}
        </button>
      </div>
    }
  `,
  styles: [`
    .tabbed-content {
      padding: 4px 0 0 0 !important;
      overflow: visible;
    }

    .tab-body {
      padding: 0;
      min-height: 160px;
    }

    .prompt-textarea {
      width: 100%;
      border-radius: 4px;
      outline: none;
      border: 1px solid rgb(118, 118, 118);
      padding: 8px;
      font-family: inherit;
      font-size: 14px;
      resize: vertical;
      box-sizing: border-box;
    }

    /* MAP-1533: space the three enrichment toggles apart with the same 10px gap
       the smap assistant's input row uses between its toggles, instead of letting
       them butt against each other as bare inline elements. */
    .toggle-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 8px;
    }

    .trial-frame {
      display: block;
      margin-top: 12px;
    }

    .web-search-cost-warning {
      color: #cc0000;
      background-color: #fae6e6;
      border-radius: 8px;
      padding: 12px 16px;
      font-size: 13px;
      line-height: 1.4;
      margin: 8px 0 0 0;
      text-align: center;
    }

    .prompt-textarea:focus {
      border-color: #3D85C6;
    }

    ::ng-deep .tab-body app-text-area textarea {
      background-color: #ffffff !important;
    }

    .document-row {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 36px;
      margin-top: 8px;
    }

    .document-icon {
      font-size: 22px;
      color: #3D85C6;
    }

    .document-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 14px;
      color: #3D85C6;
    }

    .document-remove {
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 50%;
      background: none;
      color: #5E707F;
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
    }

    .document-remove:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .document-note {
      font-size: 14px;
      color: #5E707F;
    }

    .document-hint {
      margin: 2px 0 0;
      font-size: 12px;
      color: #5E707F;
    }

    .choose-file-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fff;
      color: #3D85C6;
      font: inherit;
      font-size: 14px;
      cursor: pointer;
    }

    .choose-file-btn .material-icons {
      font-size: 18px;
    }

    .choose-file-btn:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    ::ng-deep .create-smap-tabs {
      --mat-tab-header-label-text-size: 17px;
      --mat-tab-header-inactive-label-text-color: #5E707F;
      --mat-tab-header-inactive-focus-label-text-color: #5E707F;
      --mat-tab-header-inactive-hover-label-text-color: #5E707F;
      --mat-tab-header-active-label-text-color: #3D85C6;
      --mat-tab-header-active-focus-label-text-color: #3D85C6;
      --mat-tab-header-active-hover-label-text-color: #3D85C6;
      --mat-tab-header-active-ripple-color: #3D85C6;
      --mat-tab-header-inactive-ripple-color: #3D85C6;
      --mdc-tab-indicator-active-indicator-color: #3D85C6;

      .mat-mdc-tab-header {
        margin-bottom: 0;
        border-bottom: 1px solid #e0e0e0;
      }

      .mat-mdc-tab-labels {
        justify-content: center;
      }

      .mat-mdc-tab {
        min-width: 0;
        padding: 0 20px;
        font-family: 'Oswald', sans-serif;
        font-size: 17px;
        letter-spacing: 0.5px;
        height: 48px;
        flex-grow: 0;
      }

      .mdc-tab__text-label {
        display: flex;
        align-items: center;
        gap: 0;
        font-size: 17px;
        line-height: 1;
      }

      /* Maporium icon-font glyphs sit inside the label; size them independently
         of the text so the tab keeps its 48px height. */
      .mdc-tab__text-label [class*='icon-smap-'] {
        font-size: 22px;
        line-height: 1;
      }

      .mat-mdc-tab-body-wrapper {
        background: #f6faff;
        border-radius: 0 0 8px 8px;
        min-height: 180px;
      }

      .mat-mdc-tab-body-content {
        padding: 8px 16px;
      }

      .mat-mdc-tab-header-pagination {
        display: none !important;
      }
    }
  `],
  imports: [
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    ColorSketchModule,
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatIconModule,
    TextInputComponent,
    SlideToggleInputComponent,
    SelectInputComponent,
    TextAreaComponent,
    TranslatePipe,
    HasPackageDirective,
    MatTabsModule,
    MatFormFieldModule,
    FormsModule,
    MatSlideToggleModule,
    EndTrialFrameComponent
  ],
  standalone: true
})

export class CreateNewMapDialogComponent implements OnInit {
  myForm: FormGroup;
  @ViewChild('inputField') inputField: ElementRef | undefined;
  @ViewChild('pdfFileInput') pdfFileInputRef: ElementRef<HTMLInputElement> | undefined;
  @ViewChild('templateNameInput') templateNameInput: TextInputComponent | undefined;
  @ViewChild('promptTextarea') promptTextareaRef: ElementRef<HTMLTextAreaElement> | undefined;
  templates: GraphMap[] = [];
  templateControl = new FormControl();
  templateOptionsStream: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  selectedTemplate: GraphMap | undefined;
  isMapATemplate = false;
  hasViews = false;
  hasTours = false;
  hasModels = false;

  // Tabbed create mode
  activeTabIndex = 1; // Default to CREATE WITH AI
  promptControl = new FormControl('');
  readonly documentMaxMb = DOCUMENT_MAX_MB;
  webSearchEnabled = false;
  // MAP-1533: opt-in enrichment during generation. Both require web search, so
  // turning either ON forces Search the Web ON, and turning Search the Web OFF
  // turns both back OFF. OFF by default.
  includeResourcesEnabled = false;
  includeImagesEnabled = false;

  get webSearchAvailable(): boolean {
    return !environment.production || !this.aiStateService.isUsingCustomKeys();
  }

  /** MAP-1607: web-search features are withheld for the length of the free trial. */
  get webFeaturesLocked(): boolean {
    return this.trialStateService.isOnTrial;
  }

  onTrialEnded(): void {
    this.trialStateService.markTrialEnded();
    this.cdr.detectChanges();
  }

  onWebSearchChange(checked: boolean): void {
    // Search the Web is required by both enrichment options; turning it OFF
    // turns them OFF too. Turning it ON has no effect on them.
    if (!checked) {
      this.includeResourcesEnabled = false;
      this.includeImagesEnabled = false;
    }
  }

  onIncludeResourcesChange(checked: boolean): void {
    // Enrichment needs web search; turning it ON enables Search the Web.
    if (checked) {
      this.webSearchEnabled = true;
    }
  }

  onIncludeImagesChange(checked: boolean): void {
    if (checked) {
      this.webSearchEnabled = true;
    }
  }

  publisherNameOptions: { key: string; value: string; icon: string; isSeparatorAfter?: boolean }[] = [];

  get isEditOrCopyMode(): boolean {
    return !!(this.data?.map || this.data?.isCopy);
  }

  get canSetPublisherName(): boolean {
    const userId = this.userService.getCurrentUserFromStorage()?.id;
    return !!this.data?.map && !this.data?.isCopy
      && this.data.map.owner?.id === userId
      && this.packageService.canManagePublisherName();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      map?: GraphMap,
      isCopy?: boolean,
      preSelectedTemplateId?: string,
      preSelectedStoreTemplate?: GraphMap
    } | undefined | null,
    private matDialogRef: MatDialogRef<CreateNewMapDialogComponent>,
    private cdr: ChangeDetectorRef,
    private mapService: MapService,
    private userService: UserService,
    private packageService: PackageService,
    private modelService: ModelService,
    private openaiService: OpenaiService,
    private router: Router,
    private draftUploads: DraftUploadService,
    private notificationService: NotificationService,
    private creditApiService: CreditApiService,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    protected aiStateService: AiStateService,
    private translate: TranslateService,
    private trialStateService: TrialStateService,
  ) {
    this.myForm = new FormGroup({
      name: new FormControl('', Validators.required),
      description: new FormControl(''),
      isTemplate: new FormControl(false),
      allowViewersToCreateFromTemplate: new FormControl(false),
      includeViewsAndTours: new FormControl(true),
      publisherNameOption: new FormControl('default'),
    });

    // Restore saved tab index
    const savedTab = localStorage.getItem(TAB_STORAGE_KEY);
    if (savedTab !== null) {
      const idx = parseInt(savedTab, 10);
      // The prompt and document tabs became one; an old saved index 2 means it.
      if (idx >= 0) {
        this.activeTabIndex = Math.min(idx, 1);
      }
    }
  }

  ngOnInit(): void {
    this.buildPublisherNameOptions();
    this.trialStateService.refresh();
    if (this.webFeaturesLocked) {
      this.webSearchEnabled = false;
      this.includeResourcesEnabled = false;
      this.includeImagesEnabled = false;
    }
    if (this.data?.map && !this.data?.isCopy) {
      this.myForm.patchValue({...this.data.map});
      this.myForm.get('publisherNameOption')?.setValue(this.data.map.publisherNameOption || 'default');
      this.isMapATemplate = this.data.map.isTemplate;
    } else if (this.data?.map && this.data?.isCopy) {
      // MAP-1097: default to the original smap's description so duplicating
      // keeps it unless the user explicitly types a new one.
      this.myForm.get('description')?.setValue(this.data.map.description || '');
    }

    this.templateControl.valueChanges.subscribe((id) => this.handleTemplateSelection(id));

    this.myForm.valueChanges.subscribe((value) => {
      this.isMapATemplate = value.isTemplate;
    });

    this.mapService.findTemplates().subscribe((groupedTemplates) => {
      this.templates = [
        ...groupedTemplates.owned,
        ...groupedTemplates.shared,
        ...groupedTemplates.store
      ];

      const currentUser = this.userService.getCurrentUserFromStorage();

      const templateOptions: {
        key: string;
        value: string;
        icon: string;
        tooltipText?: string;
        isSeparatorAfter?: boolean
      }[] = [];

      const addedTemplateIds = new Set<string>();

      // Add blank template as first option
      templateOptions.push({
        key: '__blank__',
        value: this.translate.instant('CREATE_MAP_DIALOG.BLANK_TEMPLATE'),
        icon: 'icon-smap-object-template',
        isSeparatorAfter: this.templates.length > 0 || !!this.data?.preSelectedStoreTemplate
      });

      // Add owned templates first
      templateOptions.push(...groupedTemplates.owned.map((template, index) => {
        addedTemplateIds.add(template.id);

        const isShared = (template.sharedWith && template.sharedWith.length > 0) ||
                        (template.userRoles && template.userRoles.length > 1);

        return {
          key: template.id,
          value: template.name,
          icon: isShared ? 'icon-smap-role-owner-shared' : 'icon-smap-role-owner',
          tooltipText: template.description,
          isSeparatorAfter: index === groupedTemplates.owned.length - 1 && (groupedTemplates.shared.length > 0 || groupedTemplates.store.length > 0)
        };
      }));

      if (groupedTemplates.shared.length > 0) {
        templateOptions.push(...groupedTemplates.shared.map((template, index) => {
          addedTemplateIds.add(template.id);

          return {
            key: template.id,
            value: template.name,
            tooltipText: template.description,
            icon: 'icon-smap-role-viewer',
            isSeparatorAfter: index === groupedTemplates.shared.length - 1 && groupedTemplates.store.length > 0
          };
        }));
      }

      if (groupedTemplates.store.length > 0) {
        const uniqueStoreTemplates = groupedTemplates.store.filter(template => !addedTemplateIds.has(template.id));

        if (uniqueStoreTemplates.length > 0) {
          templateOptions.push(...uniqueStoreTemplates.map((template) => {
            addedTemplateIds.add(template.id);
            return {
              key: template.id,
              value: template.name,
              tooltipText: template.description,
              icon: template.allowViewersToCreateFromTemplate
                  ? 'icon-smap-object-template'
                  : 'icon-smap-role-viewer'
            };
          }));
        }
      }

      if (this.data?.preSelectedStoreTemplate && !addedTemplateIds.has(this.data.preSelectedStoreTemplate.id)) {
        const storeTemplate = this.data.preSelectedStoreTemplate;
        this.templates.push(storeTemplate);
        if (templateOptions.length > 0) {
          templateOptions[templateOptions.length - 1].isSeparatorAfter = true;
        }
        templateOptions.push({
          key: storeTemplate.id,
          value: storeTemplate.name,
          tooltipText: storeTemplate.description,
          icon: 'icon-smap-object-template'
        });
      }

      this.templateOptionsStream.next(templateOptions);

      // Pre-select template if provided, otherwise select blank
      if (this.data?.preSelectedTemplateId) {
        this.templateControl.setValue(this.data.preSelectedTemplateId);
      } else if (!this.isEditOrCopyMode) {
        this.templateControl.setValue('__blank__');
      }
    });
    setTimeout(() => {
      if (this.data?.map) {
        this.mapService.getAllToursAndViewsForMap(this.data?.map?.id || '')
          .subscribe((res) => {
            this.hasTours = res.tours.length > 0;
            this.hasViews = res.views.length > 0;
            this.cdr.detectChanges();
          });

        this.modelService.getAll(this.data.map.id).subscribe((models) => {
          this.hasModels = models.nodeModels.length > 0 || models.linkModels.length > 0;
          this.cdr.detectChanges();
        });
      }
    }, 300);
  }

  selectedFileName = '';
  readonly documentGlyph = documentFormatGlyph;
  isImporting = false;

  private buildPublisherNameOptions(): void {
    const setting = this.userService.getCurrentUserFromStorage()?.publisherNameSetting;
    const settingText = setting === 'owner'
      ? this.translate.instant('CREATE_MAP_DIALOG.PUBLISHER_OWNER')
      : this.translate.instant('CREATE_MAP_DIALOG.PUBLISHER_ORGANIZATION');
    this.publisherNameOptions = [
      {key: 'default', value: this.translate.instant('CREATE_MAP_DIALOG.PUBLISHER_DEFAULT', {setting: settingText}), icon: 'icon-smap-user-settings', isSeparatorAfter: true},
      {key: 'owner', value: this.translate.instant('CREATE_MAP_DIALOG.PUBLISHER_OWNER'), icon: 'icon-smap-account-individual'},
      {key: 'organization', value: this.translate.instant('CREATE_MAP_DIALOG.PUBLISHER_ORGANIZATION'), icon: 'icon-smap-account-enterprise'}
    ];
  }

  onFileSelected() {
    const files = this.pdfFileInputRef?.nativeElement?.files;
    this.selectedFileName = files?.length ? files[0].name : '';
    this.cdr.detectChanges();
  }

  clearFile() {
    if (this.pdfFileInputRef?.nativeElement) this.pdfFileInputRef.nativeElement.value = '';
    this.selectedFileName = '';
    this.cdr.detectChanges();
  }

  onTabChange(index: number) {
    this.activeTabIndex = index;
    localStorage.setItem(TAB_STORAGE_KEY, index.toString());
  }

  focusActiveTabField(): void {
    if (this.isEditOrCopyMode) return;
    if (this.activeTabIndex === 0) {
      this.templateNameInput?.focus();
    } else if (this.activeTabIndex === 1) {
      this.promptTextareaRef?.nativeElement?.focus();
    }
  }

  isCreateEnabled(): boolean {
    switch (this.activeTabIndex) {
      case 0: // Template tab
        return this.myForm.get('name')?.valid === true;
      case 1: // AI tab: a prompt, a document, or both
        return (this.promptControl.value ?? '').trim().length >= 3 || !!this.selectedFileName;
      default:
        return false;
    }
  }

  onCreateClick() {
    if (this.isImporting) return;
    switch (this.activeTabIndex) {
      case 0:
        this.create();
        break;
      case 1:
        this.startWithAi();
        break;
    }
  }

  create() {
    if (this.myForm.valid) {
      const payload = {
        name: this.myForm.value.name,
        description: this.myForm.value.description,
        isTemplate: this.myForm.value.isTemplate,
        allowViewersToCreateFromTemplate: this.myForm.value.allowViewersToCreateFromTemplate,
        includeViewsAndTours: this.myForm.value.includeViewsAndTours,
      };
      if (this.selectedTemplate) {
        const templateToSend = {
          ...this.selectedTemplate,
          name: this.myForm.value.name,
          includeViewsAndTours: this.myForm.value.includeViewsAndTours
        };

        this.mapService.duplicate(templateToSend).subscribe((newMap) => {
          set(newMap, 'isFromTemplate', true);
          this.matDialogRef.close(newMap);
        });
      } else {
        if (!this?.data?.isCopy) {
          this.matDialogRef.close(payload);
        } else {
          if (!this.data.map) return;
          this.data.map.name = this.myForm.value.name;
          this.data.map.description = this.myForm.value.description;
          this.data.map.includeViewsAndTours = this.myForm.value.includeViewsAndTours;
          this.mapService.duplicate(this.data.map).subscribe((newMap) => {
            this.matDialogRef.close(newMap);
          });
        }
      }
    }
  }

  handleTemplateSelection(id: string) {
    if (id === '__blank__') {
      this.selectedTemplate = undefined;
      return;
    }
    const template = this.templates.find((template) => template.id === id);
    if (template) {
      this.selectedTemplate = template;
    }
  }

  edit() {
    if (!this.data) return;
    if (this.myForm.valid && this.data.map) {
      const payload = {
        ...this.data.map,
        id: this.data.map.id,
        name: this.myForm.value.name,
        description: this.myForm.value.description,
        isTemplate: this.myForm.value.isTemplate,
        allowViewersToCreateFromTemplate: this.myForm.value.allowViewersToCreateFromTemplate,
        includeViewsAndTours: this.myForm.value.includeViewsAndTours,
        publisherNameOption: this.myForm.value.publisherNameOption,
      };
      this.mapService.update(payload).subscribe(
        (map) => this.matDialogRef.close(map),
        (error) => {
          console.error('Error updating map:', error);
          alert(this.translate.instant('CREATE_MAP_DIALOG.ERROR_UPDATING_MAP', {message: error.error?.message || error.message}));
        }
      );
    }
  }

  // MAP-1533: words in the prompt that signal the user expects node images.
  // Used to catch the "top CEOs with headshots" case where the prompt asks for
  // images but the "Include images" enrichment toggle was left off.
  private static readonly IMAGE_INTENT_REGEX =
    /\b(image|images|imagery|photo|photos|picture|pictures|headshot|headshots|portrait|portraits|logo|logos|avatar|avatars|thumbnail|thumbnails|pic|pics)\b/i;

  private promptRequestsImages(prompt: string): boolean {
    return CreateNewMapDialogComponent.IMAGE_INTENT_REGEX.test(prompt);
  }

  private startWithAi() {
    const prompt = (this.promptControl.value ?? '').trim();
    const file = this.pdfFileInputRef?.nativeElement?.files?.[0] ?? null;
    if (!prompt && !file) {
      this.notificationService.warning(this.translate.instant('CREATE_MAP_DIALOG.ENTER_PROMPT_WARNING'));
      return;
    }

    // MAP-1533: the prompt asks for images but the "Include images" enrichment
    // toggle is off, so nothing would be fetched. Image enrichment costs extra
    // AI credits, so confirm before running: only enable it (and proceed) when
    // the user opts in; otherwise abort the request. Skipped when web search
    // (which enrichment requires) is unavailable, or images are already on.
    if (!this.includeImagesEnabled && this.webSearchAvailable && !this.webFeaturesLocked && this.promptRequestsImages(prompt)) {
      this.dialog.open(ConfirmationDialogComponent, {
        width: '420px',
        autoFocus: false,
        data: {
          title: this.translate.instant('CREATE_MAP_DIALOG.INCLUDE_IMAGES_CONFIRM_TITLE'),
          icon: 'image',
          message: this.translate.instant('CREATE_MAP_DIALOG.INCLUDE_IMAGES_CONFIRM_MESSAGE'),
          confirmText: this.translate.instant('CREATE_MAP_DIALOG.INCLUDE_IMAGES_CONFIRM_BUTTON'),
          cancelText: this.translate.instant('CREATE_EDIT_SMAP.ACTIONS.CANCEL'),
        }
      }).afterClosed().subscribe((result: { confirmed?: boolean } | undefined) => {
        if (result?.confirmed) {
          // Mirror onIncludeImagesChange: enrichment needs web search.
          this.includeImagesEnabled = true;
          this.webSearchEnabled = true;
          this.startDraft(file, prompt);
        }
        // Cancelled -> abort: leave the toggles untouched and do not start.
      });
      return;
    }

    this.startDraft(file, prompt);
  }

  /** Opens the conversation; the draft page sends the first turn once any document is ready. */
  private startDraft(file: File | null, prompt: string) {
    this.isImporting = true;
    this.creditApiService.ensureCredits(this.dialog, 'smap').subscribe(ok => {
      if (!ok) {
        this.isImporting = false;
        return;
      }
      const opened = (draftId: string) => {
        this.draftUploads.holdFirstTurn(draftId, {
          message: prompt,
          webSearch: this.webSearchEnabled,
          includeResources: this.includeResourcesEnabled,
          includeImages: this.includeImagesEnabled,
        });
        this.matDialogRef.close();
        this.bottomSheet.dismiss();
        void this.router.navigate(['/dashboard/draft', draftId]);
      };
      const failed = (error: any) => {
        this.isImporting = false;
        if (isPdfTooLargeError(error)) {
          showPdfTooLargeNotification(this.notificationService, this.translate);
          return;
        }
        const code = error.error?.message || error.message || '';
        const key = `DOCUMENT_DRAFT.ERRORS.${code}`;
        const translated = this.translate.instant(key);
        this.notificationService.error(
          translated !== key ? translated : (code || this.translate.instant('CREATE_MAP_DIALOG.GENERATION_ERROR_DEFAULT')),
          {title: this.translate.instant('CREATE_MAP_DIALOG.GENERATION_FAILED_TITLE'), duration: 0}
        );
        console.error('Draft error:', error);
      };

      this.openaiService.createDocumentDraft().subscribe({
        next: ({draftId}) => {
          if (!file) {
            opened(draftId);
            return;
          }
          this.openaiService.addDraftDocument(draftId, file).subscribe({
            next: ({documentId}) => {
              this.draftUploads.hold(documentId, file);
              opened(draftId);
            },
            error: failed,
          });
        },
        error: failed,
      });
    });
  }

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;
}
